import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  MailOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { PropTypes } from "@scalableminds/prop-types";
import { useQueryClient } from "@tanstack/react-query";
import AdminPage from "admin/admin_page";
import { unwrapOrThrow } from "admin/api/api_result";
import ChangeUsernameView from "admin/auth/change_username_view";
import { InviteUsersModal } from "admin/onboarding";
import { getActiveUserCount } from "admin/organization/pricing_plan_utils";
import { getEditableUsers, updateUser } from "admin/rest_api";
import { renderTeamRolesAndPermissionsForUser } from "admin/team/team_list_view";
import ExperienceModalView from "admin/user/experience_modal_view";
import PermissionsAndTeamsModalView from "admin/user/permissions_and_teams_modal_view";
import {
  Alert,
  App,
  Button,
  Col,
  Flex,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import LinkButton from "components/link_button";
import dayjs from "dayjs";
import features from "features";
import { copyToClipboard } from "libs/clipboard";
import Persistence from "libs/persistence";
import { useApi, useWkSelector } from "libs/react_hooks";
import { filterWithSearchQueryAND, localeCompareBy, scrollToTop } from "libs/utils";
import { location } from "libs/window";
import keyBy from "lodash-es/keyBy";
import React, { type Key, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router";
import type { APITeamMembership, APIUser, ExperienceMap } from "types/api_types";
import { enforceActiveOrganization } from "viewer/model/accessors/organization_accessors";
import { enforceActiveUser } from "viewer/model/accessors/user_accessor";
import { setActiveUserAction } from "viewer/model/actions/user_actions";

const { Column } = Table;
const { Search } = Input;

type ActivationFilterType = Array<"activated" | "deactivated" | "verified" | "unverified">;

const persistence = new Persistence<{
  activationFilter: ActivationFilterType;
  searchQuery: string;
}>(
  {
    searchQuery: PropTypes.string,
    activationFilter: PropTypes.arrayOf(PropTypes.string),
  },
  "userList",
);

function UserListView() {
  const { modal } = App.useApp();
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const activeUser = useWkSelector((state) => enforceActiveUser(state.activeUser));
  const activeOrganization = useWkSelector((state) =>
    enforceActiveOrganization(state.activeOrganization),
  );

  const { data: users = [], isFetching: isLoading } = useApi({
    queryKey: ["editableUsers"],
    queryFn: getEditableUsers,
    refetchOnWindowFocus: false,
  });

  const [selectedUserIds, setSelectedUserIds] = useState<Key[]>([]);
  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false);
  const [isTeamRoleModalOpen, setIsTeamRoleModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(() => location.hash === "#invite");
  const [activationFilter, setActivationFilter] = useState<ActivationFilterType>(
    () => persistence.load().activationFilter || ["activated"],
  );
  const [searchQuery, setSearchQuery] = useState(() => persistence.load().searchQuery || "");
  const [singleSelectedUser, setSingleSelectedUser] = useState<APIUser | null | undefined>(null);
  const [userToEdit, setUserToEdit] = useState<APIUser | null>(null);
  const [domainToEdit, setDomainToEdit] = useState<string | null | undefined>(null);
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);

  async function activateUser(selectedUser: APIUser, isActive: boolean = true) {
    const newUser = unwrapOrThrow(await updateUser({ ...selectedUser, isActive }));
    queryClient.setQueryData(["editableUsers"], (currentUsers: APIUser[]) =>
      currentUsers.map((user) => (selectedUser.id === user.id ? newUser : user)),
    );

    if (!isActive) {
      // Don't ask the user for the team permissions
      return;
    }
    modal.confirm({
      title: "Account was activated.",
      content:
        "If the user was activated for the first time, they will only be able to see datasets that belong to the Default team. Do you want to configure the teams and permissions of the user?",
      okText: "Configure teams and permissions",
      cancelText: "Close",
      icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
      onOk: () => {
        setSelectedUserIds([selectedUser.id]);
        setIsTeamRoleModalOpen(isActive);
      },
    });
  }

  function deactivateUser(user: APIUser): void {
    activateUser(user, false);
  }

  function handleUsersChange(updatedUsers: Array<APIUser>): void {
    queryClient.setQueryData(["editableUsers"], updatedUsers);
    setIsExperienceModalOpen(false);
    setIsTeamRoleModalOpen(false);
  }

  function closeExperienceModal(updatedUsers: Array<APIUser>): void {
    const updatedUsersMap = keyBy(updatedUsers, (u) => u.id);

    setIsExperienceModalOpen(false);
    queryClient.setQueryData(["editableUsers"], (currentUsers: APIUser[]) =>
      currentUsers.map((user) => updatedUsersMap[user.id] || user),
    );
    setSingleSelectedUser(null);
    setSelectedUserIds((singleSelectedUser) => (singleSelectedUser == null ? [] : selectedUserIds));
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>): void {
    const newSearchQuery = event.target.value;
    setSearchQuery(newSearchQuery);
    persistence.persist({ searchQuery: newSearchQuery, activationFilter });
  }

  function handleDismissActivationFilter(): void {
    setActivationFilter([]);
    persistence.persist({ searchQuery, activationFilter: [] });
  }

  function renderNewUsersAlert() {
    const now = dayjs();
    const newInactiveUsers = users.filter(
      (user) => !user.isActive && dayjs.duration(now.diff(user.created)).asDays() <= 14,
    );
    const newInactiveUsersHeader = (
      <React.Fragment>
        There are new inactive users{" "}
        <Tooltip
          title="The displayed users are inactive and were created in the past 14 days."
          placement="right"
        >
          <InfoCircleOutlined />
        </Tooltip>
      </React.Fragment>
    );
    const newInactiveUsersList = (
      <React.Fragment>
        {newInactiveUsers.map((user) => (
          <Row key={user.id} gutter={16}>
            <Col span={6}>{`${user.lastName}, ${user.firstName} (${user.email}) `}</Col>
            <Col span={4}>
              <LinkButton onClick={() => activateUser(user)} icon={<UserAddOutlined />}>
                Activate User
              </LinkButton>
            </Col>
          </Row>
        ))}
      </React.Fragment>
    );

    return newInactiveUsers.length ? (
      <Alert
        title={newInactiveUsersHeader}
        description={newInactiveUsersList}
        type="info"
        icon={<UserOutlined className="icon-margin-right" />}
        showIcon
      />
    ) : null;
  }

  function renderInviteUsersAlert() {
    const inviteUsersCallback = () => setIsInviteModalOpen(true);

    const noUsersMessage = (
      <React.Fragment>
        <a onClick={inviteUsersCallback}>Invite colleagues and collaboration partners</a>
        {" to join your organization. Share datasets and collaboratively work on annotations."}
      </React.Fragment>
    );
    return isLoading ? null : (
      <Alert
        title="Invite more users"
        description={noUsersMessage}
        type="info"
        showIcon
        action={
          <Button type="primary" onClick={inviteUsersCallback}>
            Invite Users
          </Button>
        }
      />
    );
  }

  function renderUpgradePlanAlert() {
    return (
      <Alert
        title={
          <>
            <Typography.Text strong>
              {getActiveUserCount(users)} of {activeOrganization.includedUsers} users active.
            </Typography.Text>{" "}
            Your plan is full. Only users of other paid organizations can still join as guests.{" "}
            <Link to={`/organizations/${activeUser.organization}`}>Details</Link>
          </>
        }
        type="warning"
        showIcon
        action={
          <Link to={`/organizations/${activeUser.organization}`}>
            <Button type="primary">Upgrade Plan</Button>
          </Link>
        }
      />
    );
  }

  function getAllSelectedUsers(): APIUser[] {
    if (selectedUserIds.length > 0) {
      return users.filter((user) => selectedUserIds.includes(user.id));
    }
    return [];
  }

  function onSelectUserRow(userId: string) {
    setSelectedUserIds((selectedUserIds) => {
      const indexOfUser = selectedUserIds.indexOf(userId);
      if (indexOfUser >= 0) {
        return selectedUserIds.splice(indexOfUser, 1);
      } else {
        return [...selectedUserIds, userId];
      }
    });
  }

  const hasRowsSelected = selectedUserIds.length > 0;
  const rowSelection = {
    preserveSelectedRowKeys: true,

    onChange: (selectedUserIds: Key[]) => {
      setSelectedUserIds(selectedUserIds);
    },
    getCheckboxProps: (user: APIUser) => ({
      disabled: !user.isActive,
    }),
    selectedRowKeys: selectedUserIds,
  };
  const activationFilterWarning = activationFilter.includes("activated") ? (
    <Tag closable onClose={handleDismissActivationFilter} color="blue" variant="outlined">
      Show Active Users Only
    </Tag>
  ) : null;

  const noOtherUsers = users.length < 2;
  const isNewUserInvitesDisabled = getActiveUserCount(users) >= activeOrganization.includedUsers;
  const mayChangeNameForUsers = features().isWkorgInstance === false && activeUser.isAdmin;

  return (
    <AdminPage
      title="Users"
      descriptionURI="https://docs.webknossos.org/webknossos/users/index.html"
      description="Manage members, permissions, and activation status in your organization."
      actions={
        <Space wrap size="middle">
          {hasRowsSelected ? <span>{selectedUserIds.length} selected user(s)</span> : null}
          <Button
            onClick={() => setIsTeamRoleModalOpen(true)}
            icon={<TeamOutlined />}
            disabled={!hasRowsSelected}
          >
            Edit Teams &amp; Permissions
          </Button>
          <Button
            onClick={() => {
              setIsExperienceModalOpen(true);
            }}
            icon={<TrophyOutlined />}
            disabled={!hasRowsSelected}
          >
            Change Experience
          </Button>
          <Button icon={<UserAddOutlined />} onClick={() => setIsInviteModalOpen(true)}>
            Invite {isNewUserInvitesDisabled ? "Guests" : "Users"}
          </Button>
          <InviteUsersModal
            currentUserCount={getActiveUserCount(users)}
            maxUserCountPerOrganization={activeOrganization.includedUsers}
            isOpen={isInviteModalOpen}
            organizationId={activeUser.organization}
            handleVisibleChange={(visible) => {
              setIsInviteModalOpen(visible);
            }}
          />
          {userToEdit != null ? (
            <Modal
              destroyOnHidden
              title="Change Name"
              open={editNameModalOpen}
              footer={null}
              onCancel={() => setEditNameModalOpen(false)}
            >
              <ChangeUsernameView
                onClose={() => setEditNameModalOpen(false)}
                user={userToEdit}
                setEditedUser={(editedUser: APIUser) => {
                  queryClient.setQueryData(["editableUsers"], (currentUsers: APIUser[]) =>
                    currentUsers.map((user) => (editedUser.id === user.id ? editedUser : user)),
                  );
                  if (activeUser.id === editedUser.id) {
                    dispatch(setActiveUserAction(editedUser));
                  }
                }}
              />
            </Modal>
          ) : null}
        </Space>
      }
      search={<Search allowClear onChange={handleSearch} value={searchQuery} />}
      alerts={
        <Space orientation="vertical" style={{ width: "100%" }}>
          {activationFilterWarning}
          {isNewUserInvitesDisabled ? renderUpgradePlanAlert() : null}
          {noOtherUsers && !isNewUserInvitesDisabled ? renderInviteUsersAlert() : null}
          {renderNewUsersAlert()}
        </Space>
      }
    >
      <Spin size="large" spinning={isLoading}>
        <Table
          dataSource={filterWithSearchQueryAND(
            users,
            ["firstName", "lastName", "email", "teams", (user) => Object.keys(user.experiences)],
            searchQuery,
          )}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            defaultPageSize: 50,
            onChange: scrollToTop,
          }}
          onChange={(_pagination, filters) => {
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'FilterValue' is not assignable to type '("tr... Remove this comment to see the full error(message)
            const newFilter: ActivationFilterType =
              filters.isActive != null ? filters.isActive : [];
            setActivationFilter(newFilter);
            persistence.persist({ searchQuery, activationFilter: newFilter });
          }}
          onRow={(user) => ({
            onClick: () => onSelectUserRow(user.id),
          })}
          className="large-table"
          scroll={{
            x: "max-content",
          }}
        >
          <Column
            title="Last Name"
            dataIndex="lastName"
            key="lastName"
            width={200}
            sorter={localeCompareBy<APIUser>((user) => user.lastName)}
          />
          <Column
            title="First Name"
            dataIndex="firstName"
            key="firstName"
            width={200}
            sorter={localeCompareBy<APIUser>((user) => user.firstName)}
          />
          <Column
            title="Email"
            dataIndex="email"
            key="email"
            width={320}
            sorter={localeCompareBy<APIUser>((user) => user.email)}
          />
          <Column
            title="Teams - Role"
            dataIndex="teams"
            key="teams_"
            width={250}
            render={(_teams: APITeamMembership[], user: APIUser) =>
              renderTeamRolesAndPermissionsForUser(user)
            }
          />
          <Column
            title="Experiences"
            dataIndex="experiences"
            key="experiences"
            width={250}
            render={(experiences: ExperienceMap, user: APIUser) => (
              <Space wrap>
                {Object.entries(experiences).map(([domain, value]) => (
                  <Tag key={`experience_${user.id}_${domain}`} variant="outlined">
                    <span
                      onClick={(evt) => {
                        evt.stopPropagation();
                        // If no user is selected, set singleSelectedUser. Otherwise,
                        // open the modal so that all selected users are edited.
                        setSingleSelectedUser(selectedUserIds.length > 0 ? null : user);
                        setDomainToEdit(domain);
                        setIsExperienceModalOpen(true);
                      }}
                    >
                      {domain} : {value}
                    </span>
                    <CopyOutlined
                      style={{
                        margin: "0 0 0 5px",
                      }}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        copyToClipboard(domain, "experience domain", true);
                      }}
                    />
                  </Tag>
                ))}
              </Space>
            )}
          />
          <Column
            title="Status"
            dataIndex="isActive"
            key="isActive"
            width={110}
            filters={[
              {
                text: "Activated",
                value: "activated",
              },
              {
                text: "Deactivated",
                value: "deactivated",
              },
              {
                text: "E-Mail is verified",
                value: "verified",
              },
              {
                text: "E-Mail is not verified",
                value: "unverified",
              },
            ]}
            filtered
            filteredValue={activationFilter}
            filterMultiple
            // @ts-expect-error
            onFilter={(
              value: "activated" | "deactivated" | "verified" | "unverified",
              user: APIUser,
            ) => {
              if (value === "activated") {
                return user.isActive;
              } else if (value === "deactivated") {
                return !user.isActive;
              } else if (value === "verified") {
                return user.isEmailVerified;
              } else if (value === "unverified") {
                return !user.isEmailVerified;
              }
            }}
            render={(isActive, user: APIUser) => {
              const activation = isActive ? (
                <Tooltip title="Account is activated">
                  <CheckCircleOutlined
                    className="icon-margin-right"
                    style={{
                      fontSize: 20,
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Account is not activated">
                  <Typography.Text type="danger">
                    <CloseCircleOutlined
                      className="icon-margin-right"
                      style={{
                        fontSize: 20,
                      }}
                    />
                  </Typography.Text>
                </Tooltip>
              );

              const mail = user.isEmailVerified ? (
                <Tooltip title="Email is verified">
                  <MailOutlined
                    className="icon-margin-right"
                    style={{
                      fontSize: 20,
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Email is not verified">
                  <Typography.Text type="danger">
                    <MailOutlined
                      className="icon-margin-right"
                      style={{
                        fontSize: 20,
                      }}
                    />
                  </Typography.Text>
                </Tooltip>
              );

              return (
                <>
                  {activation}
                  {mail}
                </>
              );
            }}
          />
          <Column
            title="Actions"
            key="actions"
            width={175}
            fixed="right"
            render={(__, user: APIUser) => (
              <Flex vertical align="start">
                <Link to={`/users/${user.id}/details`}>
                  <LinkButton icon={<UserOutlined />}>Show Annotations</LinkButton>
                </Link>
                {mayChangeNameForUsers ? (
                  <LinkButton
                    icon={<UserOutlined />}
                    onClick={(event) => {
                      setUserToEdit(user);
                      setEditNameModalOpen(true);
                      event.stopPropagation();
                    }}
                  >
                    Change Name
                  </LinkButton>
                ) : null}
                {user.isActive ? (
                  activeUser.isAdmin ? (
                    <LinkButton
                      onClick={(event) => {
                        event.stopPropagation();
                        deactivateUser(user);
                      }}
                      icon={<UserDeleteOutlined />}
                    >
                      Deactivate User
                    </LinkButton>
                  ) : null
                ) : (
                  <LinkButton
                    onClick={(event) => {
                      event.stopPropagation();
                      activateUser(user);
                    }}
                    icon={<UserAddOutlined />}
                  >
                    Activate User
                  </LinkButton>
                )}
              </Flex>
            )}
          />
        </Table>
      </Spin>
      {isExperienceModalOpen ? (
        <ExperienceModalView
          isOpen={isExperienceModalOpen}
          selectedUsers={singleSelectedUser ? [singleSelectedUser] : getAllSelectedUsers()}
          initialDomainToEdit={domainToEdit}
          onChange={closeExperienceModal}
          onCancel={() => {
            setIsExperienceModalOpen(false);
            setSingleSelectedUser(null);
            setDomainToEdit(null);
          }}
        />
      ) : null}
      <PermissionsAndTeamsModalView
        isOpen={isTeamRoleModalOpen}
        selectedUserIds={selectedUserIds}
        users={users}
        onChange={handleUsersChange}
        onCancel={() => setIsTeamRoleModalOpen(false)}
        activeUser={activeUser}
      />
    </AdminPage>
  );
}

export default UserListView;
